import React, { useMemo } from 'react';
import { SmartLinkWidgetType } from 'fym-common/src/db/enums';
import { SerializedSmartLinkVersionTheme } from 'fym-shared/src/smartlinks/types';
import { spotify } from 'fym-shared/src/music-services/services/Spotify';
import { appleMusic } from 'fym-shared/src/music-services/services/AppleMusic';
import { youtubeMusic } from 'fym-shared/src/music-services/services/YouTubeMusic';
import { deezer } from 'fym-shared/src/music-services/services/Deezer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay } from '@fortawesome/pro-solid-svg-icons/faPlay';
import { TypedMemo } from '../../../common/types';
import { pickerThemeLooks } from './pickerThemeLooks';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for several parts; each part uses its own slice of it */
import styles from './SMLBlockPicker.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SMLPickerPictureProps {
  type: SmartLinkWidgetType;
  theme: SerializedSmartLinkVersionTheme;
}

/** The services of the Catalog links picture, in the order of the design (b1); three rows fit the picture. */
const CATALOG_SERVICES = [spotify, appleMusic, youtubeMusic, deezer];

/** The service dot of a row: the brand colour is data of the service, not a colour of the app. */
const ServiceRow: React.FC<{ colour: string; pill?: boolean }> = ({ colour, pill = true }) => {
  const dot = useMemo(() => ({ background: colour }), [colour]);
  return (
    <span className={styles.mpRow}>
      <span className={styles.mpDot} style={dot} />
      <span className={styles.mpBar} />
      {pill && <span className={styles.mpPill} />}
    </span>
  );
};

const Button: React.FC<{ solid?: boolean }> = ({ solid = false }) => (
  <span className={solid ? styles.mpButtonSolid : styles.mpButton}>
    <i />
  </span>
);

function partsOf(type: SmartLinkWidgetType): React.ReactNode {
  switch (type) {
    case SmartLinkWidgetType.presave:
      return CATALOG_SERVICES.slice(0, 3).map((service) => (
        <ServiceRow key={service.id} colour={service.color.primary} />
      ));
    case SmartLinkWidgetType.player:
      return (
        <>
          <span className={styles.mpPlayer}>
            <span className={styles.mpPlayerCover} />
            <span className={styles.mpPlayerBar} />
          </span>
          <span className={styles.mpLine} />
        </>
      );
    case SmartLinkWidgetType.media:
      return (
        <span className={styles.mpMedia}>
          <span className={styles.mpMediaPlay}>
            <FontAwesomeIcon icon={faPlay} />
          </span>
        </span>
      );
    case SmartLinkWidgetType.presaveSubscribe:
      return (
        <>
          <span className={styles.mpTitle} />
          <ServiceRow colour={spotify.color.primary} pill={false} />
          <Button solid />
        </>
      );
    case SmartLinkWidgetType.socialLinks:
      return (
        <span className={styles.mpSocial}>
          <i />
          <i />
          <i />
          <i />
        </span>
      );
    case SmartLinkWidgetType.link:
      return (
        <>
          <Button solid />
          <Button />
        </>
      );
    case SmartLinkWidgetType.info:
      return (
        <>
          <span className={styles.mpTitle} />
          <span className={styles.mpLine} />
          <span className={styles.mpLineShort} />
        </>
      );
    case SmartLinkWidgetType.profile:
    case SmartLinkWidgetType.image:
    default:
      return (
        <>
          <span className={styles.mpAvatar} />
          <span className={styles.mpTitleShort} />
          <span className={styles.mpLineShort} />
        </>
      );
  }
}

/**
 * The picture of a picker card (design smartlink-editor b1): the block drawn in CSS on the page's
 * own theme, not a screenshot. The theme look is data of the page, so it goes in as CSS variables.
 */
const SMLPickerPictureComponent: React.FC<SMLPickerPictureProps> = ({ type, theme }) => {
  const look = useMemo(() => {
    // A theme that this build does not know yet (a newer API) draws as Dark.
    const t = pickerThemeLooks[theme] ?? pickerThemeLooks[SerializedSmartLinkVersionTheme.dark];
    return {
      '--pp-ground': t.ground,
      '--pp-ink': t.ink,
      '--pp-accent': t.accent,
      '--pp-card': t.card,
      '--pp-line': t.line,
      '--pp-btn': t.button,
      '--pp-btn-ink': t.buttonInk,
      '--pp-radius': t.radius,
      '--pp-btn-radius': t.buttonRadius,
      '--pp-avatar-radius': t.avatarRadius,
    } as React.CSSProperties;
  }, [theme]);
  return (
    <span className={styles.picture} style={look} aria-hidden>
      {partsOf(type)}
    </span>
  );
};

export const SMLPickerPicture = TypedMemo(SMLPickerPictureComponent);
